import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type InstrumentType = 'competitor_analyzer' | 'product_finder';

export interface InstrumentRun {
  id: string;
  user_id: string;
  instrument_type: InstrumentType;
  idea_id: string | null;
  inputs: Record<string, unknown>;
  results: Record<string, unknown>;
  summary_text: string | null;
  created_at: string;
}

export function useInstrumentRuns(instrumentType: InstrumentType) {
  const { user } = useAuth();
  const [runs, setRuns] = useState<InstrumentRun[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRuns = useCallback(async () => {
    if (!user) {
      setRuns([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('instrument_runs')
      .select('*')
      .eq('user_id', user.id)
      .eq('instrument_type', instrumentType)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching instrument runs:', error);
    } else {
      setRuns((data as unknown as InstrumentRun[]) || []);
    }
    setLoading(false);
  }, [user, instrumentType]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const saveRun = async (
    inputs: Record<string, unknown>,
    results: Record<string, unknown>,
    summaryText: string | null,
    ideaId?: string | null
  ): Promise<InstrumentRun | null> => {
    if (!user) return null;

    const insertData = {
      user_id: user.id,
      instrument_type: instrumentType,
      idea_id: ideaId || null,
      inputs: inputs as unknown as Record<string, never>,
      results: results as unknown as Record<string, never>,
      summary_text: summaryText,
    };

    const { data, error } = await supabase
      .from('instrument_runs')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error saving instrument run:', error);
      return null;
    }

    const newRun = data as unknown as InstrumentRun;
    setRuns(prev => [newRun, ...prev].slice(0, 10));
    return newRun;
  };

  return {
    runs,
    loading,
    saveRun,
    refetch: fetchRuns,
  };
}
